import React, { useEffect, useState } from 'react'
import transactionStore from './stores/transaction'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro' // <-- import styles to be used
import Button from '@/../components/Button'
import { Text, Radios } from '@/../components/Fields'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import DestinationSelect from '@/../components/DestinationSelect'
import { produce } from 'immer'
import destinationStore from '@/../stores/destination'
import { DateTime } from 'luxon'
import regionStore from '@/../stores/region'
import { Link, useParams, useNavigate } from 'react-router-dom'
import Table from './components/Table'

export default function () {

  const { id } = useParams()

  const [transactionToEdit, setTransactionToEdit] = useState({})
  const [showOrder, setShowOrder] = useState(false)
  const [
    updateTransaction,
    deleteTransaction,
    validate,
    errors,
    clearErrors,
  ] = transactionStore(state => [
    state.update,
    state.delete,
    state.validate,
    state.errors,
    state.clearErrors,
  ])
  const [
    destinations,
  ] = destinationStore(state => [
    state.collection,
  ])
  const [regions] = regionStore(state => [state.collection])

  const faTrash = icon({ name: 'trash' })
  const pencil = icon({ name: 'pencil' })
  const plus = icon({ name: 'plus' })
  const axe = icon({ name: 'axe' })
  const navigate = useNavigate()

  async function save(e) {
    e.preventDefault()

    if (await validate(transactionToEdit, {requireOrderNumber: false, ops: true})) {
      await updateTransaction(transactionToEdit)
      navigate('/ops')
    }
  }

  const { magento_order } = transactionToEdit ?? {}
  const { billing_address } = magento_order ?? {}
  const shipping_address = magento_order?.extension_attributes?.shipping_assignments[0]?.shipping?.address ?? {}
  // const shipping_address = {}

  async function fetchTransaction() {
    const response = await fetch(`/api/inventory_transactions/${id}.json`)
    const data = await response.json()
    setTransactionToEdit(data)
  }

  useEffect(() => {
    if (!id) return
    fetchTransaction()
  }, [id])

  return <>
    <h1 className="text-4xl mb-4">
      Editing Transaction #{transactionToEdit.id} - {DateTime.fromISO(transactionToEdit.created_at).toLocaleString(DateTime.DATETIME_MED)}
    </h1>

    {errors.length > 0 && <div className="mb-4 text-red-500 bg-red-100 p-4 rounded border border-red-200">
      <h3 className="font-bold text-lg mb-2">Oh No!</h3>
      <ul className="list-disc pl-6">
        {errors.map((error, index) => <li key={index}>{error}</li>)}
      </ul>
    </div>}
    <Text
      label="Reference"
      value={transactionToEdit?.notes ?? ''}
      className="mb-4"
      readonly
    />
    <div className='grid grid-cols-2 gap-8 mb-4'>
      <div>
        <h1 className='font-bold mb-2'>Magic Box Order Details:</h1>
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.Th>Product</Table.Th>
              <Table.Th>Qty</Table.Th>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {transactionToEdit?.items_attributes && transactionToEdit?.items_attributes?.map((item) => 
              <Table.Row key={item.id}>
                <Table.Td>{item.title}</Table.Td>
                <Table.Td className="text-right">{item.quantity}</Table.Td>
              </Table.Row>
            )}
            <Table.Row>
              <Table.Td className="text-right font-bold">Total</Table.Td>
              <Table.Td className="text-right">{transactionToEdit?.items_attributes?.reduce((sum, item) => sum + item.quantity, 0)}</Table.Td>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
      {transactionToEdit?.magento_order && 
        <div>
          <h1 className='font-bold mb-2'>Magento Order Details:</h1>
          <div className='grid grid-cols-2'>
            <div className='mb-4'>
              <h2 className='font-bold'>Billing Address</h2>
              <p className='mb-0'>{billing_address.firstname} {billing_address.lastname}</p>
              <p className='mb-0'>{billing_address.street[0]}</p>
              {billing_address.street[1] && <p>{billing_address.street[1]}</p>}
              <p className='mb-0'>{billing_address.city}, {billing_address.region}, {billing_address.postcode}</p>
              <p className='mb-0'>{billing_address.country_id}</p>
              <p className='mb-0'>T: {billing_address.telephone}</p>
              <p className='mb-0'><a href={`mailto:${transactionToEdit.magento_order.customer_email}`}>{transactionToEdit.magento_order.customer_email}</a></p>
            </div>
            <div>
              <h2 className='font-bold'>Shipping address</h2>
              <p className='mb-0'>{shipping_address.firstname} {shipping_address.lastname}</p>
              <p className='mb-0'>{shipping_address.street?.[0]}</p>
              {shipping_address.street?.[1] && <p>{shipping_address.street?.[1]}</p>}
              <p className='mb-0'>{shipping_address.city}, {shipping_address.region}, {shipping_address.postcode}</p>
              <p className='mb-0'>{shipping_address.country_id}</p>
              <p className='mb-0'>T: {shipping_address.telephone}</p>
            </div>
          </div>
          <div>
            <h2 className='font-bold mb-2'>Items Invoiced</h2>
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.Th>Product</Table.Th>
                  <Table.Th>Qty</Table.Th>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {magento_order?.items.map(item => <React.Fragment key={item.product_id}>
                  {!item.parent_item &&
                    <Table.Row>
                      <Table.Td>{item.sku}</Table.Td>
                      <Table.Td className="text-right">{item.qty_invoiced}</Table.Td>
                    </Table.Row>
                  }
                </React.Fragment>)}
                <Table.Row>
                  <Table.Td className="text-right font-bold">Total</Table.Td>
                  <Table.Td className="text-right">{magento_order.total_qty_ordered}</Table.Td>
                </Table.Row>
              </Table.Body>
            </Table>
          </div>
        </div>
      }
    </div>
    <Text
      label="Order Name"
      value={transactionToEdit?.order_name ?? ''}
      onChange={e => setTransactionToEdit(produce(transactionToEdit, draft => {
        draft.order_name = e.target.value
      }))}
      className="mb-4"
    />
    <Text
      label="Trip Year"
      value={transactionToEdit?.ops_year ?? ''}
      onChange={e => setTransactionToEdit(produce(transactionToEdit, draft => {
        draft.ops_year = e.target.value
      }))}
      className="mb-4"
    />
    <DestinationSelect
      value={transactionToEdit?.destinations_transactions_attributes ?? []}
      onChange={(destinations, destination = null) => {
        const newTransaction = {
          ...transactionToEdit,
          destinations_transactions_attributes: destinations,
        }
        if (!transactionToEdit.region_id) {
          newTransaction.region_id = regions.find(r => r.uuid == destination?.id_Region)?.value
        }
        setTransactionToEdit(newTransaction)
      }}
    />
    <div className="flex items-center justify-between">
      <Link to="/ops">
        Cancel
      </Link>
      <Button type="success" onClick={save}>
        <FontAwesomeIcon icon={icon({name: 'save'})} />
        Save
      </Button>
    </div>
  </>
}
